import { useQuery } from "@tanstack/react-query";
import { Award, Download } from "lucide-react";
import { api, getAccessToken } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { usePageTitle } from "@/lib/title";
import type { Certificate } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

async function downloadCertificate(id: string, filename: string) {
  const res = await fetch(`/api/v1.0/certificates/${id}/download`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
  });
  if (!res.ok) throw new Error("Failed to download certificate");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export function CertificatesPage() {
  usePageTitle("Certificates");
  const { notify } = useToast();
  const { data: certificates, isLoading } = useQuery({
    queryKey: ["certificates", "me"],
    queryFn: () => api.get<Certificate[]>("/api/v1.0/certificates/me"),
  });

  if (isLoading) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Certificates you&apos;ve earned by completing courses.</p>

      {!certificates?.length ? (
        <EmptyState icon={Award} title="No certificates yet" description="Complete a course to earn your first certificate." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {certificates.map((cert) => (
            <Card key={cert.id}>
              <CardBody className="flex flex-col items-center gap-3 py-8 text-center">
                <Award className="h-10 w-10 text-amber-500" />
                <h3 className="font-semibold text-slate-900">{cert.course_title}</h3>
                <p className="text-xs text-slate-400">
                  {cert.certificate_number} · {new Date(cert.issued_at).toLocaleDateString()}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    downloadCertificate(cert.id, cert.certificate_number).catch(() =>
                      notify("Failed to download certificate", "error"),
                    )
                  }
                >
                  <Download className="h-4 w-4" /> Download PDF
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
